<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { onClickOutside } from '@vueuse/core';
import TaskDatePicker from './TaskDatePicker.vue';
import { buildDueIso } from '../helpers/dueDate';

const props = defineProps({
  task: { type: Object, required: true },
});

const emit = defineEmits(['close', 'scheduled', 'expand']);

const { t } = useI18n();
const store = useStore();

const popoverRef = ref(null);

function extractTimeFromTimestamp(ts) {
  if (!ts) return null;
  const d = new Date(ts * 1000);
  const h = d.getHours();
  const m = d.getMinutes();
  if (h === 0 && m === 0) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const dateValue = ref({
  due_date: null,
  due_time: extractTimeFromTimestamp(props.task.due_date),
  recurrence_type: null,
  recurrence_interval: null,
  recurrence_unit: null,
  recurrence_ends_at: null,
  recurrence_count: null,
});
const isScheduling = ref(false);

onClickOutside(popoverRef, () => emit('close'));

function onKeydown(e) {
  if (e.key === 'Escape') emit('close');
}

async function buildNextTask() {
  const payload = {
    title: props.task.title,
    description: props.task.description,
    priority: props.task.priority,
    task_type: props.task.task_type,
    assignee_id: props.task.assignee_id,
    contact_id: props.task.contact_id,
    conversation_id: props.task.conversation_id,
    kanban_card_id: props.task.kanban_card_id,
    label_list: props.task.label_list,
    recurrence_type: 'flexible',
    recurrence_occurrences: (props.task.recurrence_occurrences ?? 0) + 1,
    due_date: buildDueIso(dateValue.value.due_date, dateValue.value.due_time),
    status: 'pending',
  };
  const result = await store.dispatch('tasks/createTask', payload);
  return result;
}

async function onSchedule() {
  if (!dateValue.value.due_date) return;
  isScheduling.value = true;
  try {
    const result = await buildNextTask();
    if (result.success) {
      emit('scheduled', result.data);
      emit('close');
    }
  } finally {
    isScheduling.value = false;
  }
}

async function onExpand() {
  if (!dateValue.value.due_date) return;
  isScheduling.value = true;
  try {
    const result = await buildNextTask();
    if (result.success) {
      emit('expand', result.data);
      emit('close');
    }
  } finally {
    isScheduling.value = false;
  }
}
</script>

<template>
  <div
    ref="popoverRef"
    class="relative z-30 w-72 bg-n-background border border-n-weak rounded-xl shadow-xl flex flex-col max-h-[90vh]"
    @keydown="onKeydown"
  >
    <!-- Cabeçalho -->
    <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
      <span class="i-lucide-repeat size-4 text-n-brand flex-shrink-0" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('TASKS.RECURRENCE.FLEXIBLE_POPOVER.TITLE') }}
      </span>
    </div>

    <!-- DatePicker sem seção de recorrência -->
    <div class="overflow-y-auto flex-1">
      <TaskDatePicker v-model="dateValue" hide-recurrence />
    </div>

    <!-- Rodapé -->
    <div class="flex items-center gap-2 px-4 py-3 border-t border-n-weak">
      <button
        type="button"
        class="flex-1 flex items-center justify-center gap-1.5 bg-n-brand text-white rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50 transition-colors hover:brightness-110"
        :disabled="!dateValue.due_date || isScheduling"
        @click="onSchedule"
      >
        {{ t('TASKS.RECURRENCE.FLEXIBLE_POPOVER.CONFIRM') }}
      </button>
      <button
        type="button"
        class="flex items-center justify-center border border-n-weak text-n-slate-11 rounded-lg px-2.5 py-1.5 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
        :disabled="!dateValue.due_date || isScheduling"
        :title="t('TASKS.RECURRENCE.FLEXIBLE_POPOVER.EXPAND_LABEL')"
        @click="onExpand"
      >
        <span class="i-lucide-chevron-down size-4" />
      </button>
    </div>
  </div>
</template>
